import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import { Select } from '@this/src/components/shared/ui/inputs/select';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import DatetimePicker from '@this/shared/datetime_picker/datetime_picker';
import DepartmentSelector from '../../shared/department_selector';
import CostAnalysisCsvDownloadArea from './cost_analysis_csv_download';

interface Props {
  onSearch: (params: SearchParams) => void;
  loading: boolean;
}

const DestinationCategories = [
  { label: 'all', name: 'すべて' },
  { label: 'domestic', name: '国内' },
  { label: 'foreign', name: '海外' }
];

type DestinationCategoryType = 'all' | 'domestic' | 'foreign';

type DateType = 'confirmed_at' | 'end_at';

export interface SearchParams {
  department_ids: string;
  destination_category: string;
  date_type: DateType;
  start_date: string;
  end_date: string;
  bill_amount_from: number | string;
  bill_amount_to: number | string;
}

const CostAnalysisSearchArea: React.FC<Props> = ({ onSearch, loading }) => {
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>([]);
  const [destinationCategory, setDestinationCategory] = useState<DestinationCategoryType>('all');
  const [dateType, setDateType] = useState<DateType>('confirmed_at');
  const [startDate, setStartDate] = useState<Moment>(moment().startOf('month'));
  const [endDate, setEndDate] = useState<Moment>(moment().endOf('month'));
  const [billAmountFrom, setBillAmountFrom] = useState<number | string>('');
  const [billAmountTo, setBillAmountTo] = useState<number | string>('');
  const styles = useMemo(() => getStyles(), []);

  const getParams = (): SearchParams => {
    return {
      department_ids: selectedDepartmentIds.join(','),
      destination_category: destinationCategory,
      date_type: dateType,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      bill_amount_from: billAmountFrom || '',
      bill_amount_to: billAmountTo || ''
    };
  };

  const handleSearch = () => {
    const params = getParams();
    onSearch(params);
  };

  useEffect(() => {
    handleSearch();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [selectedDepartmentIds]);

  const handleSearchClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleSearch();
  };

  return (
    <>
      <div style={styles.searchArea}>
        <div style={styles.section}>
          <div style={styles.title}>データ条件</div>
          <div className="flex">
            <div style={styles.searchBlock}>
              <div style={styles.searchLabel}>出張種別</div>
              <Select
                value={destinationCategory}
                onChange={e => setDestinationCategory(e.target.value as DestinationCategoryType)}
                style={{ marginBottom: 0, marginRight: '5px' }}
              >
                {DestinationCategories.map(cat => (
                  <option key={cat.label} value={cat.label}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </div>
            <div style={styles.searchBlock}>
              <div style={styles.searchLabel}>部署</div>
              <DepartmentSelector
                selectedDepartmentIds={selectedDepartmentIds}
                onSelect={ids => setSelectedDepartmentIds(ids)}
                filter
              />
            </div>
          </div>
          <div style={styles.radioButtonGroup}>
            <label className="radio">
              <input
                type="radio"
                name="date_type"
                value="confirmed_at"
                checked={dateType === 'confirmed_at'}
                onChange={() => setDateType('confirmed_at')}
              />
              出張確定日
            </label>
            <label className="radio" style={styles.radioLabelFollowing}>
              <input
                type="radio"
                name="date_type"
                value="end_at"
                checked={dateType === 'end_at'}
                onChange={() => setDateType('end_at')}
              />
              出張完了日
            </label>
          </div>
        </div>
        <div className="flex">
          <div style={styles.section}>
            <div style={styles.title}>対象期間</div>
            <div style={styles.termGroup}>
              <DatetimePicker
                value={startDate}
                onChange={d => setStartDate(d)}
                showTime={false}
                disabledDays={0}
                showPast
              />
              <span>〜</span>
              <DatetimePicker
                value={endDate}
                onChange={d => setEndDate(d)}
                showTime={false}
                disabledDays={0}
                showPast
              />
            </div>
          </div>
          <div style={styles.billAmountSection}>
            <div style={styles.title}>合計金額</div>
            <div style={styles.termGroup}>
              <input
                type="number"
                value={billAmountFrom}
                onChange={e => setBillAmountFrom(parseInt(e.target.value, 10) || '')}
                style={{ height: '24px', width: '120px', marginBottom: 0 }}
              />
              円<span style={{ lineHeight: '24px' }}>〜</span>
              <input
                type="number"
                value={billAmountTo}
                onChange={e => setBillAmountTo(parseInt(e.target.value, 10) || '')}
                style={{ height: '24px', width: '120px', marginBottom: 0 }}
              />
              円
            </div>
          </div>
        </div>
        <div style={styles.section}>
          <Button
            color="primary"
            size="small"
            onClick={handleSearchClick}
            disabled={loading}
            style={styles.searchButton}
          >
            検索
          </Button>
        </div>
        <p className="cost-analysis__search__info">
          ※請求確定していないものについては、データが最新でない可能性がございます。
        </p>
      </div>
      <CostAnalysisCsvDownloadArea getSearchParams={getParams} />
    </>
  );
};

const getStyles = () => ({
  searchArea: {
    padding: '10px',
    backgroundColor: '#eee',
    marginBottom: '10px'
  },
  section: {
    marginBottom: '5px'
  },
  billAmountSection: {
    alignItems: 'center',
    marginBottom: '0.75em',
    marginLeft: '0.75em'
  },
  searchBlock: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '20px',
    marginBottom: '5px'
  },
  searchLabel: {
    fontWeight: 'bold' as const,
    marginRight: '5px'
  },
  title: {
    fontWeight: 'bold' as const
  },
  termGroup: {
    display: 'flex'
  },
  radioButtonGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  radioLabelFollowing: {
    marginLeft: '10px'
  },
  searchButton: {
    marginLeft: '5px'
  }
});

export default CostAnalysisSearchArea;
